import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import countriesData from '../../data/countries-states-cities-data';
import { countryFilterOptions } from '../../utils/locationSearch';
import {
  ARRIVAL_MODE_OPTIONS,
  EMPTY_FOREIGN_GUEST_DETAILS,
  getForeignGuestDrawerCopy,
  PASSPORT_TYPE_OPTIONS,
  resolveCountryCode,
  validateForeignGuestDetails,
  VISA_TYPE_OPTIONS,
} from '../../utils/foreignGuestFormUtils';
import {
  BOOKING_DRAWER_ACCENT,
  createBookingDrawerOverlayProps,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
  getGuestDrawerContentSx,
  getGuestDrawerFieldSx,
  getGuestDrawerSelectSx,
  QUICK_BOOKING_DRAWER_WIDTH_SX,
} from '../../utils/bookingDrawerLayout';

function toMomentOrNull(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value;
  const m = moment(value);
  return m.isValid() ? m : null;
}

function SectionCard({ icon, title, children, isDark, accent }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(accent, isDark ? 0.24 : 0.14)}`,
        background: isDark
          ? `linear-gradient(165deg, ${alpha(accent, 0.08)} 0%, ${alpha('#0f172a', 0.45)} 60%)`
          : `linear-gradient(165deg, ${alpha(accent, 0.06)} 0%, #ffffff 50%)`,
        p: 1.25,
        mb: 1.25,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: alpha(accent, isDark ? 0.16 : 0.1),
            color: isDark ? accent : '#0f766e',
            '& svg': { fontSize: 15 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.02em' }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

export default function ForeignGuestDetailsDrawer({
  open,
  onClose,
  onSave,
  hotelCountry,
  guestCountry,
  checkInDate = null,
  initialDetails = EMPTY_FOREIGN_GUEST_DETAILS,
  initialMode = 'edit',
  widthSx = QUICK_BOOKING_DRAWER_WIDTH_SX,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = BOOKING_DRAWER_ACCENT;
  const drawerTheme = getBookingDrawerTheme(isDark);
  const copy = useMemo(() => getForeignGuestDrawerCopy(hotelCountry), [hotelCountry]);
  const overlays = useMemo(
    () => createBookingDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );
  const countries = countriesData.countries || [];

  const [mode, setMode] = useState(initialMode);
  const [details, setDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const [errors, setErrors] = useState({});
  const readOnly = mode === 'view';

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    // Nationality is always the selected guest country — never stale guest.nationality.
    const guestCode = resolveCountryCode(guestCountry);
    const priorNationality = resolveCountryCode(initialDetails.nationality);
    const priorIssuing = resolveCountryCode(initialDetails.passportIssuingCountry);
    const passportIssuingCountry =
      priorIssuing && priorIssuing !== priorNationality
        ? priorIssuing
        : guestCode || priorIssuing || '';
    setDetails({
      ...EMPTY_FOREIGN_GUEST_DETAILS,
      ...initialDetails,
      nationality: guestCode || '',
      passportIssuingCountry,
      passportIssueDate: toMomentOrNull(initialDetails.passportIssueDate),
      passportExpiryDate: toMomentOrNull(initialDetails.passportExpiryDate),
      visaIssueDate: toMomentOrNull(initialDetails.visaIssueDate),
      visaExpiryDate: toMomentOrNull(initialDetails.visaExpiryDate),
      arrivedInCountryOn:
        toMomentOrNull(initialDetails.arrivedInCountryOn) || toMomentOrNull(checkInDate),
    });
    setErrors({});
  }, [open, initialDetails, guestCountry, checkInDate, initialMode]);

  const setField = (key, value) => {
    setDetails((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const countryValue = (codeOrName) =>
    countries.find(
      (c) =>
        c.code === codeOrName ||
        c.name === codeOrName ||
        String(c.name || '').toLowerCase() === String(codeOrName || '').toLowerCase()
    ) || null;

  const handleSave = () => {
    const guestCode = resolveCountryCode(guestCountry);
    const payload = {
      ...details,
      nationality: guestCode || details.nationality || '',
    };
    const result = validateForeignGuestDetails(payload);
    setErrors(result.errors);
    if (!result.ok) return;
    onSave?.(payload);
  };

  const fieldSx = useMemo(() => getGuestDrawerFieldSx(isDark), [isDark]);
  const selectSx = useMemo(() => getGuestDrawerSelectSx(isDark), [isDark]);
  const contentSx = useMemo(() => getGuestDrawerContentSx(isDark), [isDark]);

  const dateFieldProps = (errorKey) => ({
    textField: {
      fullWidth: true,
      required: true,
      error: !!errors[errorKey],
      helperText: errors[errorKey] || ' ',
      sx: fieldSx,
    },
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Nested over walk-in / reservation drawers — avoid FocusTrap fighting App focus helper.
      disableEnforceFocus
      disableRestoreFocus
      ModalProps={{
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      PaperProps={{
        sx: getBookingDrawerPaperSx(isDark, {
          width: widthSx,
          maxWidth: '100vw',
        }),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            ...getBookingDrawerHeaderSx(isDark),
            px: 2,
            py: 1.25,
            background: isDark
              ? `linear-gradient(135deg, ${alpha(accent, 0.16)} 0%, ${drawerTheme.headerBg} 55%)`
              : `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, #f8fffc 50%, #ffffff 100%)`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                {copy.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: drawerTheme.subtleText, display: 'block', mt: 0.25 }}
              >
                {copy.subtitle}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close foreign guest details">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            ...overlays.fieldShellSx,
            ...contentSx,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.95 : 1 }}>
            <SectionCard
              icon={<PublicIcon fontSize="inherit" />}
              title="Nationality"
              isDark={isDark}
              accent={accent}
            >
              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name}
                filterOptions={countryFilterOptions}
                slotProps={overlays?.autocompleteSlotProps}
                value={countryValue(details.nationality)}
                disabled
                readOnly
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nationality"
                    required
                    error={!!errors.nationality}
                    helperText={
                      errors.nationality || 'Matches the Country selected on the guest form'
                    }
                    sx={fieldSx}
                  />
                )}
              />
            </SectionCard>

            <SectionCard
              icon={<BadgeOutlinedIcon fontSize="inherit" />}
              title="Passport details"
              isDark={isDark}
              accent={accent}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    required
                    label="Passport number"
                    value={details.passportNumber}
                    onChange={(e) => setField('passportNumber', e.target.value.toUpperCase())}
                    error={!!errors.passportNumber}
                    helperText={errors.passportNumber || ' '}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth required error={!!errors.passportType} sx={selectSx}>
                    <InputLabel>Passport type</InputLabel>
                    <Select
                      label="Passport type"
                      value={details.passportType || 'ordinary'}
                      onChange={(e) => setField('passportType', e.target.value)}
                      MenuProps={overlays?.menuProps}
                    >
                      {PASSPORT_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Passport issue date"
                    value={details.passportIssueDate}
                    onChange={(v) => setField('passportIssueDate', v)}
                    slotProps={dateFieldProps('passportIssueDate')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Passport expiry date"
                    value={details.passportExpiryDate}
                    onChange={(v) => setField('passportExpiryDate', v)}
                    slotProps={dateFieldProps('passportExpiryDate')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Passport issue place"
                    value={details.passportIssuePlace}
                    onChange={(e) => setField('passportIssuePlace', e.target.value)}
                    error={!!errors.passportIssuePlace}
                    helperText={errors.passportIssuePlace || ' '}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    filterOptions={countryFilterOptions}
                    slotProps={overlays?.autocompleteSlotProps}
                    value={countryValue(details.passportIssuingCountry)}
                    onChange={(_, value) => setField('passportIssuingCountry', value?.code || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Passport issuing country"
                        required
                        error={!!errors.passportIssuingCountry}
                        helperText={errors.passportIssuingCountry || ' '}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              icon={<BadgeOutlinedIcon fontSize="inherit" />}
              title="Visa details"
              isDark={isDark}
              accent={accent}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    required
                    label="Visa number"
                    value={details.visaNumber}
                    onChange={(e) => setField('visaNumber', e.target.value)}
                    error={!!errors.visaNumber}
                    helperText={errors.visaNumber || ' '}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth required error={!!errors.visaType} sx={selectSx}>
                    <InputLabel>Visa type</InputLabel>
                    <Select
                      label="Visa type"
                      value={details.visaType || 'tourist'}
                      onChange={(e) => setField('visaType', e.target.value)}
                      MenuProps={overlays?.menuProps}
                    >
                      {VISA_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Visa issue date"
                    value={details.visaIssueDate}
                    onChange={(v) => setField('visaIssueDate', v)}
                    slotProps={dateFieldProps('visaIssueDate')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Visa expiry date"
                    value={details.visaExpiryDate}
                    onChange={(v) => setField('visaExpiryDate', v)}
                    slotProps={dateFieldProps('visaExpiryDate')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Visa issue place"
                    value={details.visaIssuePlace}
                    onChange={(e) => setField('visaIssuePlace', e.target.value)}
                    error={!!errors.visaIssuePlace}
                    helperText={errors.visaIssuePlace || ' '}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Visa issuing authority"
                    value={details.visaIssuingAuthority}
                    onChange={(e) => setField('visaIssuingAuthority', e.target.value)}
                    error={!!errors.visaIssuingAuthority}
                    helperText={errors.visaIssuingAuthority || ' '}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              icon={<FlightTakeoffIcon fontSize="inherit" />}
              title="Arrival details"
              isDark={isDark}
              accent={accent}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Arrival from (city / country)"
                    value={details.arrivalFrom}
                    onChange={(e) => setField('arrivalFrom', e.target.value)}
                    error={!!errors.arrivalFrom}
                    helperText={errors.arrivalFrom || ' '}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!errors.arrivalMode} sx={selectSx}>
                    <InputLabel>Mode of arrival</InputLabel>
                    <Select
                      label="Mode of arrival"
                      value={details.arrivalMode || 'air'}
                      onChange={(e) => setField('arrivalMode', e.target.value)}
                      MenuProps={overlays?.menuProps}
                    >
                      {ARRIVAL_MODE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Flight / train number"
                    value={details.arrivalTransportNumber}
                    onChange={(e) => setField('arrivalTransportNumber', e.target.value)}
                    helperText=" "
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={copy.arrivalDateLabel}
                    value={details.arrivedInCountryOn}
                    onChange={(v) => setField('arrivedInCountryOn', v)}
                    slotProps={dateFieldProps('arrivedInCountryOn')}
                  />
                </Grid>
              </Grid>
            </SectionCard>
            </Box>
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            ...getBookingDrawerFooterSx(isDark),
            px: 1.75,
            py: 1.25,
            display: 'flex',
            gap: 1,
            width: '100%',
          }}
        >
          {readOnly ? (
            <>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  borderColor: accent,
                  color: isDark ? accent : '#0f766e',
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setMode('edit')}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  backgroundColor: accent,
                  color: '#042f2e',
                  boxShadow: `0 6px 14px ${alpha(accent, 0.3)}`,
                  '&:hover': { backgroundColor: accent, filter: 'brightness(0.97)' },
                }}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  if (initialMode === 'edit') onClose();
                  else setMode('view');
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  borderColor: accent,
                  color: isDark ? accent : '#0f766e',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  backgroundColor: accent,
                  color: '#042f2e',
                  boxShadow: `0 6px 14px ${alpha(accent, 0.3)}`,
                  '&:hover': { backgroundColor: accent, filter: 'brightness(0.97)' },
                }}
              >
                {copy.saveLabel}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
